import React, { useState, useEffect } from 'react';
import toast from "react-hot-toast";

import ENDPOINTS from "../../services/Endpoints";
import newInstance from "../../services/newInstance";
import UserModel from './UserModel';
import Pagination from '../../components/Pagination';
import Spinner from '../../components/Spinner/Spinner';
import PopModal from '../../components/Modals/PopModal';
import { formatDate, displayUserRole } from '../../utils/Helpers';
import { User, MyUsers, UserParams } from '../../interfaces/Shared';


const Users = () => {
    const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
    const [activeModal, setActiveModal] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [toggleActivate, setToggleActivate] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [usersList, setUsersList] = useState<MyUsers>({
        totalPages: 1,
        totalUsers: 1,
        page: 1,
        limit: 5,
        users: [],
      })
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const searchParams: UserParams = {
        page: usersList.page,
        limit: usersList.limit,
      }
    
      const indexOfLastEntry = searchParams.page * searchParams.limit
      const indexOfFirstEntry = indexOfLastEntry - searchParams.limit

    const handleToggleEditUser = (userToEdit: User) => {
        setSelectedUser(userToEdit);
        setActiveDropdown(null);
    };

    const toggleDropdown = (index: number) => {
        setActiveDropdown(activeDropdown === index ? null : index);
    };

    const getUsers = async (searchUsersParams: UserParams) => {
        setIsFetching(true);
        try {
            const response = await newInstance.get(ENDPOINTS.getUsers, {
                params: { ...searchUsersParams },
              });

            if (response.status === 200) {
                setUsersList(response.data);
            }
            setIsFetching(false);
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || "An error occurred during fetching";
            toast.error(errorMessage, {
              position: "top-center",
              duration: 3000,
              className: "danger",
            });
            setIsFetching(false);
        }
    }

    const handlePageChange = (page: number) => {
        setUsersList({ ...usersList, page: page })
        getUsers({ ...searchParams, page: page })
      }

    const handleCloseUserCreation = () => {
        setActiveModal(false)
        getUsers(searchParams);
        setActiveDropdown(null)
    }

    const handleCloseUserEdition = () => {
        setSelectedUser(null)
        getUsers(searchParams);
        setActiveDropdown(null)
    }

    const handleUserStatus = async (userToUpdate: User) => {
        setLoading(true);
        try {
            const response = await newInstance.put(`${ENDPOINTS.updateUser}/${userToUpdate?.id}/status`, {
                status: !userToUpdate.isActive,
            });
            if (response.status === 200) {
                const successMessage = response?.data?.message;
                toast.success(successMessage, {
                  position: "top-center",
                  duration: 3000,
                  className: "success",
                });
                getUsers(searchParams)
                setToggleActivate(null)
                setActiveDropdown(null)
            }
        } catch (error:any) {
            const errorMessage = error.response?.data?.message || error.message || "An error occurred during fetching";
            toast.error(errorMessage, {
              position: "top-center",
              duration: 3000,
              className: "danger",
            });
        } finally {
            setLoading(false);
        }
      }
    
    useEffect(() => {
        getUsers(searchParams);
    }, []);

    return (
        <div className=" w-screen min-h-screen py-16 px-16">
            <div className="mb-4 text-sm text-gray-500">
            Dashboard <span className="text-blue-600">Users</span>
            </div>
            <h1 className="text-3xl font-bold mb-4">Users</h1>
            <div className="flex justify-end mb-4">
                <button className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 cursor-pointer" onClick={() => setActiveModal(true)} >
                    Create User
                </button>
            </div>

            <div className="">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead>
                    <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 border-b text-gray-600">Name</th>
                    <th className="px-4 py-3 border-b text-gray-600">Role</th>
                    <th className="px-4 py-3 border-b text-gray-600">Status</th>
                    <th className="px-4 py-3 border-b text-gray-600">Created At</th>
                    <th className="px-4 py-3 border-b text-gray-600">Actions</th>
                    </tr>
                </thead>
                {isFetching && (
                    <div className="w-full flex items-center justify-center">
                        <Spinner />
                    </div>
                )}
                {!isFetching && usersList.users.length === 0 ? (
                    <tbody>
                    <tr>
                        <td colSpan={5} className="text-center py-4">No users found</td>
                    </tr>
                    </tbody>
                ) : (
                <tbody>
                    {usersList.users.map((user, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 flex items-center space-x-3">
                            <div className="w-8 md:w-12 h-8 md:h-12 text-white bg-dark-brand rounded-full flex items-center justify-center bg-main cursor-pointer">
                                <p className="text-lg md:text-xl text-yellow-extended font-bold">{user.firstName?.charAt(0).toUpperCase()}</p>
                                <p className="text-lg md:text-xl text-yellow-extended font-bold">{user.lastName?.charAt(0).toUpperCase()}</p>
                            </div>
                            <div>
                                <div className="text-gray-800 font-semibold">
                                    {user.firstName} {user.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{displayUserRole(user.rolePermissionsId)}</td>
                        <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-sm ${user.isActive? "bg-green-100 text-green-700": "bg-red-100 text-red-700"}`}>
                                {user.isActive ? "Active" : "Inactive"}
                            </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                            {user.createdAt && formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-gray-700 relative">
                            <div className="relative">
                                <button onClick={() => toggleDropdown(index)} className="p-1 rounded-full hover:bg-gray-100" >
                                    <svg className="w-6 h-6 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 13a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2zM5 13a1 1 0 100-2 1 1 0 000 2z" />
                                    </svg>
                                </button>
                                {activeDropdown === index && (
                                    <div className="absolute right-0 w-48 bg-white rounded-md shadow-lg z-50 border">
                                        <button className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-t-md" onClick={() => handleToggleEditUser(user)} >
                                            Edit
                                        </button>
                                        <button className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100" onClick={() => setToggleActivate(user) } >
                                           {user.isActive? 'Deactivate': 'Activate'} 
                                        </button>
                                    </div>
                                )}
                            </div>
                        </td>
                    </tr>
                    ))}
                </tbody>
                )}
                </table>
            </div>
            <div className="mt-4">
                {usersList.users.length > 0 && (
                    <Pagination
                        currentPage={usersList.page}
                        totalPages={usersList.totalPages}
                        onPageChange={handlePageChange}
                        indexOfFirstEntry={indexOfFirstEntry}
                        indexOfLastEntry={indexOfLastEntry}
                        totalEntries={usersList.totalUsers}
                    />
                )}
            </div>

            {toggleActivate && (
                <PopModal
                    title={ `You are about to ${toggleActivate.isActive ? 'deactivate' : 'activate'} this User`}
                    message={`Are you sure you want to ${toggleActivate?.isActive ? 'deactivate' : 'activate'}
                    this user? This user will ${toggleActivate?.isActive ? 'not' : ''} be able to access the system.`}
                    onClose={() => setToggleActivate(null)}
                    onContinue={() => handleUserStatus(toggleActivate)}
                    loading={loading}
                />
            )}

            {activeModal && (
                <UserModel handleClose={handleCloseUserCreation} />
            )}

            {selectedUser && (
                <UserModel handleClose={handleCloseUserEdition} selectedUser={selectedUser} />
            )}
        </div>
    );
};

export default Users;
